import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNotify, useTranslate, useLocale } from 'react-admin';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionButton } from 'src/routes/components';

import { useFocusMatchConfig } from './hooks/useFocusMatchConfig';
import type {
  AddScheduleDialogProps,
  FocusMatchCardState,
  FocusMatchLiveOpsCard,
  KpiLikeStat,
  MatchLite,
  RuleDraft,
  RulePanelProps,
  ScheduleDraft,
  ScheduleTableProps,
  TranslateFn,
} from './types';
import {
  flattenMatchesByStage,
  formatInt,
  formatUsdc,
  getErrorMessage,
  getMatchOptionLabel,
  getMatchStatusText,
  getMatchTitle,
  getTeamName,
  hasScheduleChanged,
  isRuleDraftEqual,
  toRuleDraft,
  toScheduleDraft,
} from './utils';

const I18N_PREFIX = 'module.worldCup.focusMatch';

const tFocusMatch = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

const CARD_STATE_COLOR: Record<
  FocusMatchCardState,
  'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error'
> = {
  order_open: 'success',
  order_closed: 'default',
  in_progress: 'warning',
  cancelled: 'error',
  settled: 'info',
  awaiting: 'default',
};

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const {
    liveOps,
    schedules,
    rule,
    matchesByStage,
    loading,
    mutating,
    refetchAll,
    bulkUpdateSchedules,
    updateRule,
  } = useFocusMatchConfig();

  const [activeLiveIndex, setActiveLiveIndex] = useState(0);
  const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null);
  const [scheduleDrafts, setScheduleDrafts] = useState<ScheduleDraft[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rule) {
      setRuleDraft(toRuleDraft(rule));
    }
  }, [rule]);

  useEffect(() => {
    setScheduleDrafts(schedules.map(toScheduleDraft));
  }, [schedules]);

  useEffect(() => {
    const cardCount = liveOps?.cards.length ?? 0;

    if (cardCount && activeLiveIndex > cardCount - 1) {
      setActiveLiveIndex(0);
    }
  }, [activeLiveIndex, liveOps?.cards.length]);

  const originalRuleDraft = useMemo(() => (rule ? toRuleDraft(rule) : null), [rule]);

  const isRuleDirty = useMemo(
    () => !isRuleDraftEqual(ruleDraft, originalRuleDraft),
    [originalRuleDraft, ruleDraft]
  );

  const changedSchedules = useMemo(
    () => scheduleDrafts.filter((item) => !item.isLocked && hasScheduleChanged(item)),
    [scheduleDrafts]
  );

  const isOrderChanged = useMemo(() => {
    if (scheduleDrafts.length !== schedules.length) return true;
    return scheduleDrafts.some((item, index) => String(item.id) !== String(schedules[index]?.id));
  }, [scheduleDrafts, schedules]);

  const hasUnsavedChanges = isRuleDirty || changedSchedules.length > 0 || isOrderChanged;

  const scheduledMatchIds = useMemo(
    () => new Set(scheduleDrafts.map((item) => String(item.match.id))),
    [scheduleDrafts]
  );

  const availableMatches = useMemo(
    () =>
      flattenMatchesByStage(matchesByStage).filter(
        (match) => !scheduledMatchIds.has(String(match.id))
      ),
    [matchesByStage, scheduledMatchIds]
  );

  const handlePoolChange = useCallback((id: string, value: string) => {
    setScheduleDrafts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, poolUsdc: value } : item))
    );
  }, []);

  const handleRecoveryRatioChange = useCallback((id: string, value: number) => {
    setScheduleDrafts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, recoveryRatio: value } : item))
    );
  }, []);

  const handleReorder = useCallback((newRows: ScheduleDraft[]) => {
    setScheduleDrafts(newRows);
  }, []);

  const handleDiscard = useCallback(() => {
    if (!window.confirm(tFocusMatch(t, 'confirm.discard'))) return;

    if (rule) {
      setRuleDraft(toRuleDraft(rule));
    }

    setScheduleDrafts(schedules.map(toScheduleDraft));
  }, [rule, schedules, t]);

  const handleSave = useCallback(async () => {
    if (!ruleDraft) return;

    setSaving(true);

    try {
      if (changedSchedules.length || isOrderChanged) {
        await bulkUpdateSchedules({
          items: scheduleDrafts.map((item, index) => {
            const base = {
              matchId: String(item.match.id),
              poolUsdc: String(item.isLocked ? item.originalPoolUsdc : item.poolUsdc),
              sortWeight: index,
              recoveryRatio: Number(item.recoveryRatio ?? 0),
            };
            return item.id.startsWith('new-') ? base : { id: String(item.id), ...base };
          }),
        });
      }

      if (isRuleDirty) {
        await updateRule(ruleDraft);
      }

      await refetchAll();

      notify(tFocusMatch(t, 'notify.saveSuccess'), { type: 'success' });
    } catch (err) {
      notify(getErrorMessage(err) || tFocusMatch(t, 'notify.saveFailed'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [
    bulkUpdateSchedules,
    changedSchedules.length,
    isOrderChanged,
    isRuleDirty,
    notify,
    refetchAll,
    ruleDraft,
    scheduleDrafts,
    t,
    updateRule,
  ]);

  const handleRemoveSchedule = (id: string) => {
    setScheduleDrafts((prev) => prev.filter((item) => String(item.id) !== String(id)));
  };

  const handleAddSchedule = useCallback(
    async (payload: any) => {
      const match = availableMatches.find((item) => String(item.id) === String(payload.matchId));

      if (!match) return;

      setScheduleDrafts((prev) => [
        ...prev,
        {
          id: `new-${Date.now()}`,
          match,
          poolUsdc: String(payload.poolUsdc ?? ''),
          originalPoolUsdc: '',
          sortWeight: prev.length,
          originalSortWeight: prev.length,
          isLocked: false,
          cardState: 'awaiting',
          totalVouchers: 0,
          userVouchers: 0,
          mirrorVouchers: 0,
          whitelistVouchers: 0,
          recoveryRatio: payload.recoveryRatio ?? 0,
          originalRecoveryRatio: 0,
        },
      ]);
    },
    [availableMatches]
  );

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tFocusMatch(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupFocusMatchConfig') }]}
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tFocusMatch(t, 'description')}
      </Typography>

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <LiveOpsCarousel
            cards={liveOps?.cards ?? []}
            activeIndex={activeLiveIndex}
            onChange={setActiveLiveIndex}
          />

          <ScheduleTable
            rows={scheduleDrafts}
            onPoolChange={handlePoolChange}
            onRecoveryRatioChange={handleRecoveryRatioChange}
            onReorder={handleReorder}
            onRemove={handleRemoveSchedule}
            onOpenAdd={() => setAddOpen(true)}
          />

          <RulePanel value={ruleDraft} onChange={setRuleDraft} />
        </Stack>
      )}

      <AddScheduleDialog
        open={addOpen}
        matches={availableMatches}
        loading={mutating}
        onClose={() => setAddOpen(false)}
        onSubmit={async (payload) => {
          await handleAddSchedule(payload);
          setAddOpen(false);
        }}
      />

      {hasUnsavedChanges ? (
        <SaveBar loading={saving || mutating} onDiscard={handleDiscard} onSave={handleSave} />
      ) : null}
    </DashboardContent>
  );
};

const PanelHeader = memo(
  ({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Box>
        <Typography variant="h6">{title}</Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
  )
);

PanelHeader.displayName = 'PanelHeader';

const LiveOpsCarousel = memo(
  ({
    cards,
    activeIndex,
    onChange,
  }: {
    cards: FocusMatchLiveOpsCard[];
    activeIndex: number;
    onChange: (index: number) => void;
  }) => {
    const t = useTranslate();
    const theme = useTheme();
    const card = cards[activeIndex];

    const goPrev = useCallback(() => {
      onChange(activeIndex <= 0 ? Math.max(cards.length - 1, 0) : activeIndex - 1);
    }, [activeIndex, cards.length, onChange]);

    const goNext = useCallback(() => {
      onChange(activeIndex >= cards.length - 1 ? 0 : activeIndex + 1);
    }, [activeIndex, cards.length, onChange]);

    return (
      <Card sx={{ p: 3 }}>
        <PanelHeader
          title={tFocusMatch(t, 'live.title')}
          subtitle={tFocusMatch(t, 'live.desc')}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={cards.length ? `${activeIndex + 1} / ${cards.length}` : '0 / 0'}
                size="small"
                variant="outlined"
              />
              <IconButton size="small" disabled={!cards.length} onClick={goPrev}>
                <Iconify icon="eva:arrow-ios-back-fill" />
              </IconButton>
              <IconButton size="small" disabled={!cards.length} onClick={goNext}>
                <Iconify icon="eva:arrow-ios-forward-fill" />
              </IconButton>
            </Stack>
          }
        />

        <Box sx={{ mt: 2 }}>
          {card ? <LiveOpsCard card={card} /> : <EmptyText text={tFocusMatch(t, 'live.empty')} />}
        </Box>

        {cards.length > 1 ? (
          <Stack direction="row" justifyContent="center" spacing={0.8} sx={{ mt: 2 }}>
            {cards.map((item, index) => (
              <Box
                key={item.scheduleId}
                onClick={() => onChange(index)}
                sx={{
                  width: index === activeIndex ? 30 : 8,
                  height: 8,
                  borderRadius: 999,
                  cursor: 'pointer',
                  bgcolor:
                    index === activeIndex
                      ? theme.palette.primary.main
                      : theme.palette.action.disabledBackground,
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Stack>
        ) : null}
      </Card>
    );
  }
);

LiveOpsCarousel.displayName = 'LiveOpsCarousel';

const LiveOpsCard = memo(({ card }: { card: FocusMatchLiveOpsCard }) => {
  const t = useTranslate();
  const locale = useLocale();
  const theme = useTheme();

  const mapStage = (item: FocusMatchLiveOpsCard) => {
    const stageLabel = item.stage || item.stageLabel || '';
    if (stageLabel === 'group') {
      return tFocusMatch(t, 'stage.group', { group: item.groupName || '--' });
    }
    return tFocusMatch(t, `stage.${stageLabel}`) || stageLabel || '--';
  };

  const stats: KpiLikeStat[] = [
    { label: tFocusMatch(t, 'live.totalTickets'), value: formatInt(card.totalTickets) },
    { label: tFocusMatch(t, 'live.totalUsers'), value: formatInt(card.totalUsers) },
  ];

  const sideColors = {
    home: theme.palette.secondary.main,
    draw: theme.palette.warning.main,
    away: theme.palette.info.main,
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {mapStage(card)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {card.status === 'live'
              ? tFocusMatch(t, 'match.liveMinute', { minute: card.liveMinute ?? '--' })
              : tFocusMatch(t, `cardState.${card.cardState}_desc`)}
          </Typography>
        </Box>

        <Chip
          label={tFocusMatch(t, `cardState.${card.cardState}`)}
          color={CARD_STATE_COLOR[card.cardState] ?? 'default'}
          size="small"
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{
          py: 2,
          mt: 2,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <TeamName team={card.homeTeam} placeholder={card.homePlaceholder} />
        <Typography variant="body2" color="text.secondary" fontWeight={700}>
          VS
        </Typography>
        <TeamName team={card.awayTeam} placeholder={card.awayPlaceholder} />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {tFocusMatch(t, 'live.settlementHint')}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          mt: 1,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'action.hover',
          position: 'relative',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {tFocusMatch(t, 'live.pool')}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {formatUsdc(card.poolUsdc)}
        </Typography>
        {card.cardState !== 'awaiting' && (
          <Chip
            label={tFocusMatch(t, 'live.fixed')}
            size="small"
            sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}
          />
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        <SideBar
          label={`${getTeamName(card.homeTeam, locale) || tFocusMatch(t, 'live.home')} Win`}
          value={card.sides.home}
          color={sideColors.home}
        />
        {(card.stageLabel === 'group' || card.stage === 'group') && (
          <SideBar
            label={tFocusMatch(t, 'live.draw')}
            value={card.sides.draw}
            color={sideColors.draw}
          />
        )}
        <SideBar
          label={`${getTeamName(card.awayTeam) || tFocusMatch(t, 'live.away')} Win`}
          value={card.sides.away}
          color={sideColors.away}
        />
      </Box>

      <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
        {stats.map((item) => (
          <Typography key={item.label} variant="caption" color="text.secondary">
            {item.label}:{' '}
            <Typography component="span" variant="caption" fontWeight={700} color="text.primary">
              {item.value}
            </Typography>
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
});

LiveOpsCard.displayName = 'LiveOpsCard';

const SideBar = memo(
  ({
    label,
    value,
    color,
  }: {
    label: string;
    value: { tickets: number; users: number; pct: number };
    color: string;
  }) => {
    const pct = Math.max(0, Math.min(100, Number(value.pct) || 0));

    return (
      <Box sx={{ mt: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.7 }}>
          <Typography variant="body2" fontWeight={600}>
            {label}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {formatInt(value.tickets)} · {pct.toFixed(0)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 8,
            borderRadius: 999,
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
            },
          }}
        />
      </Box>
    );
  }
);

SideBar.displayName = 'SideBar';

const SortableScheduleRow = memo(
  ({
    row,
    onPoolChange,
    onRecoveryRatioChange,
    onRemove,
    t,
  }: {
    row: ScheduleDraft;
    onPoolChange: (id: string, value: string) => void;
    onRecoveryRatioChange: (id: string, value: number) => void;
    onRemove: (id: string) => void;
    t: TranslateFn;
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: row.id,
    });

    const mapStage = (item: MatchLite) => {
      const stageLabel = item.stage || item.stageLabel || '';
      if (stageLabel === 'group') {
        return tFocusMatch(t, 'stage.group', { group: item.groupName || '--' });
      }
      return tFocusMatch(t, `stage.${stageLabel}`) || stageLabel || '--';
    };

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
      background: isDragging ? 'rgba(0,0,0,0.04)' : undefined,
    };

    return (
      <TableRow ref={setNodeRef} style={style} hover>
        <TableCell sx={{ width: 48, p: 0.5 }}>
          <IconButton
            size="small"
            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
            {...attributes}
            {...listeners}
          >
            <Iconify icon="eva:menu-2-outline" />
          </IconButton>
        </TableCell>

        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={500}>
              {getMatchTitle(row.match)}
            </Typography>
            {row.isLocked ? (
              <Chip
                icon={<Iconify icon="eva:lock-fill" />}
                label={tFocusMatch(t, 'schedule.locked')}
                color="warning"
                size="small"
                variant="outlined"
              />
            ) : null}
          </Stack>
        </TableCell>

        <TableCell>
          <Chip label={mapStage(row.match) || '--'} size="small" variant="outlined" />
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {getMatchStatusText(row.match, t)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight={600}>
            {Number(row.totalVouchers ?? 0).toLocaleString('en-US')}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            {Number(row.userVouchers ?? 0).toLocaleString('en-US')}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            {Number(row.mirrorVouchers ?? 0).toLocaleString('en-US')}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            {Number(row.whitelistVouchers ?? 0).toLocaleString('en-US')}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 200 }}>
          <MoneyInput
            value={row.poolUsdc}
            disabled={row.isLocked || row.cardState !== 'awaiting'}
            required
            onChange={(value) => onPoolChange(row.id, value)}
          />
        </TableCell>
        <TableCell align="center" sx={{ width: 140 }}>
          <RecoveryRatioInput
            value={Number(row.recoveryRatio ?? 0)}
            disabled={row.isLocked || row.cardState !== 'awaiting'}
            onChange={(value) => onRecoveryRatioChange(row.id, value)}
          />
        </TableCell>
        <TableCell align="right" sx={{ width: 100 }}>
          <PermissionButton resource="world-cup-focus-match" action="write">
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => onRemove(row.id)}
              disabled={row.isLocked || row.cardState !== 'awaiting'}
            >
              {tFocusMatch(t, 'schedule.remove')}
            </Button>
          </PermissionButton>
        </TableCell>
      </TableRow>
    );
  }
);

SortableScheduleRow.displayName = 'SortableScheduleRow';

const ScheduleTable = memo(
  ({
    rows,
    onPoolChange,
    onRecoveryRatioChange,
    onReorder,
    onRemove,
    onOpenAdd,
  }: ScheduleTableProps) => {
    const t = useTranslate();

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
          const oldIndex = rows.findIndex((r) => r.id === active.id);
          const newIndex = rows.findIndex((r) => r.id === over.id);
          onReorder(arrayMove(rows, oldIndex, newIndex));
        }
      },
      [rows, onReorder]
    );

    return (
      <Card sx={{ p: 3 }}>
        <PanelHeader
          title={tFocusMatch(t, 'schedule.title')}
          subtitle={tFocusMatch(t, 'schedule.desc')}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={onOpenAdd}
            >
              {tFocusMatch(t, 'schedule.add')}
            </Button>
          }
        />

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>{tFocusMatch(t, 'schedule.match')}</TableCell>
                <TableCell>{tFocusMatch(t, 'schedule.stage')}</TableCell>
                <TableCell>{tFocusMatch(t, 'schedule.kickoff')}</TableCell>
                <TableCell align="center">{tFocusMatch(t, 'schedule.totalVouchers')}</TableCell>
                <TableCell align="center">{tFocusMatch(t, 'schedule.userVouchers')}</TableCell>
                <TableCell align="center">{tFocusMatch(t, 'schedule.mirrorVouchers')}</TableCell>
                <TableCell align="center">{tFocusMatch(t, 'schedule.whitelistVouchers')}</TableCell>
                <TableCell>{tFocusMatch(t, 'schedule.pool')}</TableCell>
                <TableCell align="center">{tFocusMatch(t, 'schedule.recoveryRatio')}</TableCell>
                <TableCell align="right">{tFocusMatch(t, 'schedule.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  <SortableContext
                    items={rows.map((r) => r.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {rows.map((row) => (
                      <SortableScheduleRow
                        key={row.id}
                        row={row}
                        onPoolChange={onPoolChange}
                        onRecoveryRatioChange={onRecoveryRatioChange}
                        onRemove={onRemove}
                        t={t}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                <TableRow>
                  <TableCell colSpan={10}>
                    <EmptyText text={tFocusMatch(t, 'schedule.empty')} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" icon={<Iconify icon="eva:info-fill" />} sx={{ mt: 2 }}>
          {tFocusMatch(t, 'schedule.note')}{' '}
          <Box component="strong" sx={{ color: 'warning.dark' }}>
            {tFocusMatch(t, 'schedule.lockNote')}
          </Box>
        </Alert>
      </Card>
    );
  }
);

ScheduleTable.displayName = 'ScheduleTable';

const RulePanel = memo(({ value, onChange }: RulePanelProps) => {
  const t = useTranslate();

  if (!value) return null;

  return (
    <Card sx={{ p: 3 }}>
      <PanelHeader title={tFocusMatch(t, 'rule.title')} subtitle={tFocusMatch(t, 'rule.desc')} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mt: 2.5,
        }}
      >
        <NumberField
          label={tFocusMatch(t, 'rule.openHours')}
          value={value.openHoursBeforeKickoff}
          suffix={tFocusMatch(t, 'unit.hour')}
          required
          onChange={(next) => onChange({ ...value, openHoursBeforeKickoff: next })}
        />

        <NumberField
          label={tFocusMatch(t, 'rule.cutoffMinutes')}
          value={value.cutoffMinutesBeforeKickoff}
          suffix={tFocusMatch(t, 'unit.minute')}
          required
          onChange={(next) => onChange({ ...value, cutoffMinutesBeforeKickoff: next })}
        />

        <NumberField
          label={tFocusMatch(t, 'rule.perUserStakeCap')}
          value={value.perUserStakeCap}
          suffix={tFocusMatch(t, 'unit.ticketUnlimited')}
          required
          min={0}
          onChange={(next) => onChange({ ...value, perUserStakeCap: next })}
        />

        <NumberField
          label={tFocusMatch(t, 'rule.perMatchStakeCap')}
          value={value.perMatchStakeCap}
          suffix={tFocusMatch(t, 'unit.ticketUnlimited')}
          required
          min={0}
          onChange={(next) => onChange({ ...value, perMatchStakeCap: next })}
        />
      </Box>
    </Card>
  );
});

RulePanel.displayName = 'RulePanel';

const AddScheduleDialog = memo(
  ({ open, matches, loading, onClose, onSubmit }: AddScheduleDialogProps) => {
    const t = useTranslate();
    const [matchId, setMatchId] = useState('');
    const [poolUsdc, setPoolUsdc] = useState('100000');
    const [recoveryRatio, setRecoveryRatio] = useState(0);

    useEffect(() => {
      if (!open) {
        setMatchId('');
        setPoolUsdc('100000');
        setRecoveryRatio(0);
      }
    }, [open]);

    const selectedMatch = useMemo(
      () => matches.find((item) => String(item.id) === String(matchId)) ?? null,
      [matchId, matches]
    );

    const mapStage = (item: MatchLite) => {
      const stageLabel = item.stage || item.stageLabel || '';
      if (stageLabel === 'group') {
        return tFocusMatch(t, 'stage.group', { group: item.groupName || '--' });
      }
      return tFocusMatch(t, `stage.${stageLabel}`) || stageLabel || '--';
    };

    const handleSubmit = useCallback(async () => {
      if (!selectedMatch) return;

      await onSubmit({
        matchId: String(selectedMatch.id),
        poolUsdc,
        recoveryRatio,
      });
    }, [onSubmit, poolUsdc, recoveryRatio, selectedMatch]);

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6">{tFocusMatch(t, 'addDialog.title')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {tFocusMatch(t, 'addDialog.desc')}
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            paddingTop: 1,
          }}
        >
          <Stack spacing={2.5}>
            <Autocomplete
              fullWidth
              options={matches}
              value={selectedMatch}
              onChange={(_, next) => setMatchId(next ? String(next.id) : '')}
              getOptionLabel={(option) => getMatchOptionLabel(option, t)}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              noOptionsText={tFocusMatch(t, 'addDialog.placeholder')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tFocusMatch(t, 'addDialog.match')}
                  placeholder={tFocusMatch(t, 'addDialog.placeholder')}
                  helperText={tFocusMatch(t, 'addDialog.availableCount', { count: matches.length })}
                />
              )}
            />

            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {tFocusMatch(t, 'addDialog.pool')}
              </Typography>
              <MoneyInput value={poolUsdc} required onChange={setPoolUsdc} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {tFocusMatch(t, 'addDialog.poolHint')}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                {tFocusMatch(t, 'addDialog.recoveryRatio')}
              </Typography>
              <RecoveryRatioInput value={recoveryRatio} onChange={setRecoveryRatio} />
            </Box>

            <Paper
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: 'background.neutral',
                minHeight: 76,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {selectedMatch ? (
                <>
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                    <TeamName
                      team={selectedMatch.homeTeam}
                      placeholder={selectedMatch.homePlaceholder}
                    />
                    <Typography variant="body2" color="text.secondary" fontWeight={700}>
                      VS
                    </Typography>
                    <TeamName
                      team={selectedMatch.awayTeam}
                      placeholder={selectedMatch.awayPlaceholder}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 1 }}
                  >
                    {mapStage(selectedMatch)} · {getMatchStatusText(selectedMatch, t)} ·{' '}
                    {formatUsdc(poolUsdc)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  {tFocusMatch(t, 'addDialog.previewEmpty')}
                </Typography>
              )}
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" disabled={loading} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <PermissionButton resource="world-cup-focus-match" action="write">
            <Button variant="contained" disabled={loading || !selectedMatch} onClick={handleSubmit}>
              {tFocusMatch(t, 'addDialog.submit')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    );
  }
);

AddScheduleDialog.displayName = 'AddScheduleDialog';

const SaveBar = memo(
  ({
    loading,
    onDiscard,
    onSave,
  }: {
    loading: boolean;
    onDiscard: () => void;
    onSave: () => void;
  }) => {
    const t = useTranslate();

    return (
      <Paper
        elevation={8}
        sx={{
          position: 'sticky',
          bottom: 16,
          mt: 3,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {tFocusMatch(t, 'saveBar.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {tFocusMatch(t, 'saveBar.desc')}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" disabled={loading} onClick={onDiscard}>
            {tFocusMatch(t, 'actions.discard')}
          </Button>
          <PermissionButton resource="world-cup-focus-match" action="write">
            <Button variant="contained" disabled={loading} onClick={onSave}>
              {loading ? t('common.saving') : tFocusMatch(t, 'actions.save')}
            </Button>
          </PermissionButton>
        </Stack>
      </Paper>
    );
  }
);

SaveBar.displayName = 'SaveBar';

const MoneyInput = memo(
  ({
    value,
    disabled,
    required,
    onChange,
  }: {
    value: string;
    disabled?: boolean;
    required?: boolean;
    onChange: (value: string) => void;
  }) => {
    const t = useTranslate();
    const trimmed = (value ?? '').trim();
    const num = Number(trimmed);
    const isInvalid =
      !disabled && (!trimmed ? Boolean(required) : !Number.isFinite(num) || num <= 0);

    return (
      <TextField
        size="small"
        fullWidth
        required={required}
        value={value}
        disabled={disabled}
        error={isInvalid}
        helperText={isInvalid ? t('ra.validation.required') : undefined}
        onChange={(event) => {
          const raw = event.target.value;
          const stripped = raw.replace(/[^\d.]/g, '');
          const parts = stripped.split('.');
          const cleaned = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : stripped;
          onChange(cleaned);
        }}
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        inputProps={{ inputMode: 'decimal' }}
        style={{ minWidth: 150 }}
      />
    );
  }
);

MoneyInput.displayName = 'MoneyInput';

const RecoveryRatioInput = memo(
  ({
    value,
    onChange,
    disabled,
  }: {
    value: number;
    onChange: (value: number) => void;
    disabled?: boolean;
  }) => {
    const [text, setText] = useState<string>(() => formatRatio(value));

    useEffect(() => {
      const externalNum = Number.isFinite(value) ? value : 0;
      const currentNum = Number(text.replace(',', '.'));
      if (!Number.isFinite(currentNum) || currentNum !== externalNum) {
        setText(formatRatio(externalNum));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <TextField
        size="small"
        fullWidth
        value={text}
        disabled={disabled}
        onChange={(e) => {
          const raw = e.target.value.replace(/\D/g, '');
          if (raw === '') {
            setText('');
            onChange(0);
            return;
          }
          const num = Number(raw);
          if (!Number.isFinite(num) || num < 0 || num > 99) return;
          setText(String(num));
          onChange(num);
        }}
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        style={{ minWidth: 100 }}
      />
    );
  }
);

RecoveryRatioInput.displayName = 'RecoveryRatioInput';

function formatRatio(num: number): string {
  if (!Number.isFinite(num)) return '0';
  return String(Math.trunc(num));
}

const NumberField = memo(
  ({
    label,
    value,
    suffix,
    required,
    min = 1,
    onChange,
  }: {
    label: string;
    value: number;
    suffix?: string;
    required?: boolean;
    min?: number;
    onChange: (value: number) => void;
  }) => {
    const t = useTranslate();
    const isInvalid = required && (!Number.isFinite(value) || value < min);

    return (
      <TextField
        label={label}
        type="number"
        size="small"
        fullWidth
        required={required}
        value={value}
        error={isInvalid}
        helperText={isInvalid ? t('ra.validation.required') : undefined}
        onKeyDown={(event) => {
          if (['e', 'E', '+', '-'].includes(event.key)) {
            event.preventDefault();
          }
        }}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === '') {
            onChange(undefined as unknown as number);
            return;
          }
          const next = Number(raw);
          if (Number.isFinite(next)) {
            onChange(next);
          }
        }}
        InputProps={{
          endAdornment: suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined,
        }}
        inputProps={{ min, inputMode: 'numeric' }}
      />
    );
  }
);

NumberField.displayName = 'NumberField';

const TeamName = memo(
  ({ team, placeholder }: { team: MatchLite['homeTeam']; placeholder?: string | null }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {team?.flagEmoji ? <Typography sx={{ fontSize: 18 }}>{team.flagEmoji}</Typography> : null}
      <Typography variant="subtitle1" fontWeight={700}>
        {team ? getTeamName(team) : placeholder || '--'}
      </Typography>
    </Stack>
  )
);

TeamName.displayName = 'TeamName';

const EmptyText = memo(({ text }: { text: string }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
));

EmptyText.displayName = 'EmptyText';
